import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import styled from 'styled-components'

declare const window: any

type Props = {
    cloudName: string
    uploadPreset: string
    showPoweredBy?: boolean
    allowedFormats?: string[]
    maxFileSize?: number
    maxImageWidth?: number
    maxImageHeight?: number
    sources?: string[]
    defaultSource?: string
    multiple?: boolean
    maxFiles?: number
    cropping?: string
    croppingAspectRatio?: number
    publicId?: string
    folder?: string
    tags?: string[]
    resourceType?: string
    contextAlt?: string
    contextCaption?: string
    buttonClass?: string
    buttonCaption?: string
    onUploadSuccess: (image: any) => void
}

export const CloudinaryUploader = (props: Props): JSX.Element => {
    const {
        cloudName,
        uploadPreset,
        showPoweredBy,
        allowedFormats,
        maxFileSize,
        maxImageWidth,
        maxImageHeight,
        sources,
        defaultSource,
        multiple,
        maxFiles,
        cropping,
        croppingAspectRatio,
        publicId,
        folder,
        tags,
        resourceType,
        contextAlt,
        contextCaption,
        buttonClass,
        buttonCaption,
        onUploadSuccess,
    } = props
    const [uploadedImage, setUploadedImage] = useState(null)

    const open = (options) => {
        console.log('open')
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            window.cloudinary.openUploadWidget(options, (error, result) => {
                if (error) {
                    reject(error)
                    return false
                }
                if (!result || result.length === 0) {
                    reject(new Error('No result from Cloudinary'))
                    return false
                }
                if (result.event === 'success') {
                    result = result.info
                    if (!Array.isArray(result)) result = [result]
                    result = result.map((image) => {
                        image.url = `https://${image.url.replace('http://', '')}`
                        return image
                    })

                    if (options.returnJustUrl) result = result.map((image) => image.url)

                    if (!options.multiple) result = result[0]

                    resolve(result)
                }
                return true
            })
        })
    }

    const getUploadOptions = () => {
        let options: any
        options = {
            cloud_name: cloudName,
            upload_preset: uploadPreset,
            language: 'en',
            // text: locale,
        }
        options.sources = sources
        options.multiple = multiple

        if (maxFiles) {
            options.max_files = maxFiles
        }

        if (cropping && cropping === 'server') {
            options.cropping = cropping
        }

        if (croppingAspectRatio) {
            options.cropping_aspect_ratio = croppingAspectRatio
        }

        if (publicId) {
            options.public_id = publicId
        }

        if (folder) {
            options.folder = folder
        }

        if (tags && tags.length > 0) {
            options.tags = tags
        }

        if (resourceType) {
            options.resourceType = resourceType
        }

        if (allowedFormats) {
            options.allowedFormats = allowedFormats
        }

        let context: any
        context = {}
        if (contextAlt) {
            context.alt = contextAlt
        }

        if (contextCaption) {
            context.caption = contextCaption
        }

        if (Object.keys(context).length > 0) {
            options.context = context
        }

        return options
    }

    const setUploadResult = (uploadedImage) => {
        console.log('uploadedImage', uploadedImage)
        setUploadedImage({
            bytes: uploadedImage.bytes,
            createdAt: uploadedImage.created_at,
            etag: uploadedImage.etag,
            format: uploadedImage.format,
            height: uploadedImage.height,
            path: uploadedImage.path,
            publicId: uploadedImage.public_id,
            resourceType: uploadedImage.resource_type,
            secureUrl: uploadedImage.secure_url,
            signature: uploadedImage.signature,
            tags: uploadedImage.tags,
            thumbnailLargeUrl: uploadedImage.thumbnail_url.replace('h_60,w_90', 'h_366,w_650'),
            thumbnailUrl: uploadedImage.thumbnail_url,
            type: uploadedImage.type,
            url: uploadedImage.url,
            version: uploadedImage.version,
            width: uploadedImage.width,
        })
        if (onUploadSuccess) onUploadSuccess(uploadedImage)
    }

    const handleClick = (ev) => {
        try {
            const options = getUploadOptions()
            console.log(options)
            window.cloudinary.openUploadWidget(options, (error, result) => {
                if (error) {
                    console.error(error)
                    return false
                }

                if (!result || result.length === 0) {
                    console.error('No result from Cloudinary')
                    return false
                }
                if (!error && result && result.event === 'success') {
                    const uploadedImage = result.info
                    setUploadResult(uploadedImage)
                }
            })
        } catch (e) {
            console.error(e)
            return false
        }
    }

    if (!cloudName || !uploadPreset) throw new Error('cloudName and uploadPreset props must be set')

    const uploaderId = `uploader_${uuidv4()}`

    return (
        <Container>
            <section>
                <div className="cloudinary-image-thumbnail">
                    {uploadedImage && uploadedImage.thumbnailLargeUrl && (
                        <img src={uploadedImage.thumbnailLargeUrl} alt="" />
                    )}
                </div>
                <button
                    type="button"
                    id={uploaderId}
                    className={buttonClass}
                    onClick={(ev) => handleClick(ev)}
                >
                    {buttonCaption}
                </button>
            </section>
        </Container>
    )
}

CloudinaryUploader.defaultProps = {
    showPoweredBy: false,
    sources: ['local', 'url', 'camera'],
    defaultSource: 'local',
    multiple: false,
    maxFiles: null,
    cropping: null,
    publicId: null,
    folder: null,
    tags: null,
    resourceType: 'auto',
    contextAlt: null,
    contextCaption: null,
    allowedFormats: ['png', 'jpg', 'jpeg', 'pdf'],
    maxFileSize: 15500000,
    maxImageWidth: null,
    maxImageHeight: null,
    buttonClass: 'cloudinary-button-security-seal',
    buttonCaption: 'Upload image',
}

const Container = styled.div`
    margin-top: 10px;
`
